import type { HeroMusicVisualControls } from "./HeroMusicVisualControls";

import { getOverlayAngle, getRotationForIndex, roundOverlayValue } from "./AlbumCoverGeometry";
import { clampValue } from "./HeroMusicUtils";
import { getHeroMusicOverlayPerspective } from "./HeroMusicVisualControls";

type Point2D = {
  x: number;
  y: number;
};

type Point3D = Point2D & {
  z: number;
};

type LocalCoverPoint = {
  u: number;
  v: number;
};

type ProjectedCoverBounds = {
  bottom: number;
  left: number;
  right: number;
  top: number;
};

type HeroMusicSkeletonGeometryOptions = {
  controls: HeroMusicVisualControls;
  coverCount: number;
  height: number;
  rotationIndex?: number;
  samplesPerEdge?: number;
  width: number;
};

export type HeroMusicSkeletonCoverPath = {
  averageZ: number;
  bounds: ProjectedCoverBounds;
  d: string;
  pointCount: number;
  positionIndex: number;
};

const DEFAULT_EDGE_SAMPLE_COUNT = 32;
const MIN_CORNER_SAMPLE_COUNT = 16;
const MIN_SURFACE_RADIUS = 0.01;
const MIN_CAMERA_DEPTH = 0.01;
const MAX_TANGENT_RATIO = 0.985;

export function getHeroMusicSkeletonCoverPaths({
  controls,
  coverCount,
  height,
  rotationIndex = 0,
  samplesPerEdge = DEFAULT_EDGE_SAMPLE_COUNT,
  width,
}: HeroMusicSkeletonGeometryOptions) {
  if (!Number.isFinite(width) || !Number.isFinite(height)) return [];
  if (width <= 0 || height <= 0 || coverCount <= 0) return [];

  const rotationY = getRotationForIndex(rotationIndex, coverCount);

  return Array.from({ length: coverCount }, (_, positionIndex) => {
    const angle = getOverlayAngle(rotationY, positionIndex, coverCount, controls.coverAngleOffset);
    const worldPoints = getCoverBoundaryPoints(angle, controls, samplesPerEdge);
    const projectedPoints = worldPoints
      .map(point => projectPoint(point, controls, width, height))
      .filter((point): point is Point3D => point !== null);

    if (projectedPoints.length === 0) return null;

    const averageZ = worldPoints.reduce((sum, point) => sum + point.z, 0) / worldPoints.length;

    return {
      averageZ,
      bounds: getProjectedBounds(projectedPoints),
      d: getSvgPath(projectedPoints),
      pointCount: projectedPoints.length,
      positionIndex,
    } satisfies HeroMusicSkeletonCoverPath;
  })
    .filter((coverPath): coverPath is HeroMusicSkeletonCoverPath => coverPath !== null)
    .sort((a, b) => a.averageZ - b.averageZ);
}

function getCoverBoundaryPoints(angle: number, controls: HeroMusicVisualControls, samplesPerEdge: number) {
  const halfWidth = controls.coverWidth / 2;
  const halfHeight = controls.coverHeight / 2;
  const borderRadius =
    Math.min(controls.coverWidth, controls.coverHeight) * clampValue(controls.coverBorderRadius, 0, 0.5);
  const resolvedSamplesPerEdge = Math.max(1, Math.floor(samplesPerEdge));
  const cornerSampleCount = Math.max(MIN_CORNER_SAMPLE_COUNT, Math.ceil(resolvedSamplesPerEdge / 2));
  const localPoints: LocalCoverPoint[] = [];

  appendLocalEdge(
    localPoints,
    { u: -halfWidth + borderRadius, v: halfHeight },
    { u: halfWidth - borderRadius, v: halfHeight },
    resolvedSamplesPerEdge,
  );
  appendLocalCorner(
    localPoints,
    { u: halfWidth - borderRadius, v: halfHeight - borderRadius },
    Math.PI / 2,
    0,
    borderRadius,
    cornerSampleCount,
  );
  appendLocalEdge(
    localPoints,
    { u: halfWidth, v: halfHeight - borderRadius },
    { u: halfWidth, v: -halfHeight + borderRadius },
    resolvedSamplesPerEdge,
  );
  appendLocalCorner(
    localPoints,
    { u: halfWidth - borderRadius, v: -halfHeight + borderRadius },
    0,
    -Math.PI / 2,
    borderRadius,
    cornerSampleCount,
  );
  appendLocalEdge(
    localPoints,
    { u: halfWidth - borderRadius, v: -halfHeight },
    { u: -halfWidth + borderRadius, v: -halfHeight },
    resolvedSamplesPerEdge,
  );
  appendLocalCorner(
    localPoints,
    { u: -halfWidth + borderRadius, v: -halfHeight + borderRadius },
    -Math.PI / 2,
    -Math.PI,
    borderRadius,
    cornerSampleCount,
  );
  appendLocalEdge(
    localPoints,
    { u: -halfWidth, v: -halfHeight + borderRadius },
    { u: -halfWidth, v: halfHeight - borderRadius },
    resolvedSamplesPerEdge,
  );
  appendLocalCorner(
    localPoints,
    { u: -halfWidth + borderRadius, v: halfHeight - borderRadius },
    Math.PI,
    Math.PI / 2,
    borderRadius,
    cornerSampleCount,
  );

  return localPoints.map(point => projectLocalCoverPointToCylinder(point, angle, controls));
}

function appendLocalEdge(
  points: LocalCoverPoint[],
  from: LocalCoverPoint,
  to: LocalCoverPoint,
  samplesPerEdge: number,
) {
  const startIndex = points.length === 0 ? 0 : 1;

  for (let index = startIndex; index <= samplesPerEdge; index++) {
    const progress = index / samplesPerEdge;

    points.push({
      u: from.u + (to.u - from.u) * progress,
      v: from.v + (to.v - from.v) * progress,
    });
  }
}

function appendLocalCorner(
  points: LocalCoverPoint[],
  center: LocalCoverPoint,
  startAngle: number,
  endAngle: number,
  radius: number,
  sampleCount: number,
) {
  if (radius <= 0) return;

  for (let index = 1; index <= sampleCount; index++) {
    const progress = index / sampleCount;
    const angle = startAngle + (endAngle - startAngle) * progress;

    points.push({
      u: center.u + Math.cos(angle) * radius,
      v: center.v + Math.sin(angle) * radius,
    });
  }
}

function projectLocalCoverPointToCylinder(point: LocalCoverPoint, angle: number, controls: HeroMusicVisualControls) {
  const surfaceRadius = Math.max(MIN_SURFACE_RADIUS, controls.radius);
  const rotationCos = Math.cos(controls.coverRotationZ);
  const rotationSin = Math.sin(controls.coverRotationZ);
  const rotatedU = point.u * rotationCos - point.v * rotationSin;
  const rotatedV = point.u * rotationSin + point.v * rotationCos;
  const tangentRatio = clampValue(rotatedU / surfaceRadius, -MAX_TANGENT_RATIO, MAX_TANGENT_RATIO);
  const surfaceAngle = angle + Math.asin(tangentRatio);

  return {
    x: Math.cos(surfaceAngle) * surfaceRadius,
    y: controls.coverOffsetY + rotatedV,
    z: Math.sin(surfaceAngle) * surfaceRadius,
  };
}

function projectPoint(point: Point3D, controls: HeroMusicVisualControls, width: number, height: number) {
  const cameraDepth = controls.cameraZ - point.z;

  if (cameraDepth <= MIN_CAMERA_DEPTH) return null;

  const focalLength = getHeroMusicOverlayPerspective(controls, height);
  const perspectiveScale = focalLength / cameraDepth;

  return {
    x: roundOverlayValue(width / 2 + point.x * perspectiveScale),
    y: roundOverlayValue(height / 2 + (controls.cameraY - point.y) * perspectiveScale),
    z: point.z,
  };
}

function getProjectedBounds(points: Point2D[]) {
  return points.reduce(
    (bounds, point) => ({
      bottom: Math.max(bounds.bottom, point.y),
      left: Math.min(bounds.left, point.x),
      right: Math.max(bounds.right, point.x),
      top: Math.min(bounds.top, point.y),
    }),
    {
      bottom: Number.NEGATIVE_INFINITY,
      left: Number.POSITIVE_INFINITY,
      right: Number.NEGATIVE_INFINITY,
      top: Number.POSITIVE_INFINITY,
    },
  );
}

function getSvgPath(points: Point2D[]) {
  const [firstPoint, ...restPoints] = points;

  if (!firstPoint) return "";

  return `M ${formatPoint(firstPoint)} ${restPoints.map(point => `L ${formatPoint(point)}`).join(" ")} Z`;
}

function formatPoint(point: Point2D) {
  return `${point.x} ${point.y}`;
}
